import { effect } from "@preact/signals";

/**
 * @typedef {object} Storage
 * @property {(key: string, value: string) => void} set
 * @property {(key: string) => string | null} get
 * @property {(key: string) => void} delete
 * @property {(fn: (key: string, value: string | null) => void) => () => void} [listen]
 */

/** @type {Storage} */
export const LocalStorage = {
	set: (key, value) => localStorage.setItem(key, value),
	get: key => localStorage.getItem(key),
	delete: key => localStorage.removeItem(key),
	listen: fn => {
		/** @param {StorageEvent} ev */
		function listener(ev) {
			if (ev.key) fn(ev.key, ev.newValue);
		}
		window.addEventListener("storage", listener);
		return () => window.removeEventListener("storage", listener);
	},
};

/** @type {Storage} */
export const Cookie = {
	set: (key, value) => {
		let cookie = `${encodeURIComponent(key)}=${encodeURIComponent(value)}`;
		cookie += "; max-age=31536000; path=/; secure; samesite=strict";

		document.cookie = cookie;
	},
	get: key => {
		const cookies = document.cookie.split(";");

		for (const cookie of cookies) {
			const [k, v = ""] = cookie.trim().split("=");
			if (!k) continue;

			try {
				if (decodeURIComponent(k) === key) return decodeURIComponent(v);
			} catch (e) {
				console.warn(e);
			}
		}

		return null;
	},
	delete: key => {
		document.cookie = `${encodeURIComponent(key)}=; path=/; expires=Thu, 01 Jan 1970 00:00:01 GMT`;
	},
};

/**
 * Parse a value from local storage (a string or null) into a valid value.
 *
 * @template T
 * @param {string | null} stored The stored value
 * @param {T} fallback A fallback value in case the stored value is invalid
 * @param {(x: unknown) => T} parse A function to parse unkown stored values
 */
function parseFromStorage(stored, fallback, parse) {
	// if the value is an empty string or null, return the initial value
	if (!stored) return fallback;

	try {
		// try to parse and return a value from the JSON string
		return parse(JSON.parse(stored));
	} catch {
		// if parsing fails, return the initial value
		return fallback;
	}
}

/**
 * Persist a signal to local storage. The signal's value when this function is called is treated as
 * the "default" value; if the stored value is absent or invalid, it will fall back to the default.
 *
 * @template T
 * @param {import("@preact/signals").Signal<T>} signal The signal to persist.
 * @param {string} key The storage key to use.
 * @param {object} [options]
 * @param {(x: unknown) => T} [options.parse] A function that parses stored values into signal
 *   values.
 * @param {Storage} [options.storage]
 * @returns {() => void} Clean up any effects or listeners.
 */
export function persist(signal, key, options = {}) {
	const { parse = (/** @type {any} */ x) => x, storage = LocalStorage } = options;

	// the initial value
	const initial = signal.value;

	// the JSON of the initial value, for later comparison
	const cmp = JSON.stringify(initial);

	// set the signal value to the parsed localStorage value
	signal.value = parseFromStorage(storage.get(key), initial, parse);

	/**
	 * @param {string} k
	 * @param {string | null} v
	 */
	function onStorage(k, v) {
		// ignore events for other keys
		if (k !== key) return;
		signal.value = parseFromStorage(v, initial, parse);
	}

	// whenever the value changes, save it to localStorage
	const dispose = effect(() => {
		const json = JSON.stringify(signal.value);
		// if the JSON-ified value is exactly the same as the original, remove it
		if (json === cmp) storage.delete(key);
		// otherwise store the JSON in localStorage
		else storage.set(key, json);
	});

	// when the local storage changes…
	const unlisten = storage.listen?.(onStorage);

	// return a cleanup function
	return () => {
		dispose();
		unlisten?.();
	};
}
